// @flow
import React from 'react';

import { isEmpty } from 'lodash';
import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import { ChatIneligibleCopy, DisabledComposerListItemCopy } from 'constants/CopyConstants';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import OrganizationRecipientItem from './OrganizationRecipientItem';
import LimitedGroupRecipientItem from './LimitedGroupRecipientItem';
import GroupRecipientItem from './GroupRecipientItem';
import GroupChatRecipientItem from './GroupChatRecipientItem';
import IndividualRecipientItem from './IndividualRecipientItem';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text';
import { ComposerContext } from './ComposerContext';

type Props = {
  recipient: Recipient[],
  recipientIsSelected: boolean,
  districtIsSelected: boolean,
  nonDistrictRecipientIsSelected?: boolean,
  selectionLength: number,
};

export default class RecipientItem extends React.Component<Props, *> {
  render() {
    const {
      recipient,
      recipientIsSelected,
      nonDistrictRecipientIsSelected,
      selectionLength,
      districtIsSelected,
    } = this.props;
    const organizationRecipients: OrganizationRecipient[] = [];
    const groupChatRecipients: GroupChatRecipient[] = [];
    const individualRecipients: IndividualRecipient[] = [];
    const groupRecipients: GroupRecipient[] = [];

    for (let i = 0; i < recipient.length; i++) {
      switch (recipient[i].type) {
        case RECIPIENT_TYPES.ORGANIZATION_RECIPIENT:
          organizationRecipients.push(recipient[i]);
          break;
        case RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT:
          groupChatRecipients.push(recipient[i]);
          break;
        case RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT:
          individualRecipients.push(recipient[i]);
          break;
        case RECIPIENT_TYPES.GROUP_RECIPIENT:
          groupRecipients.push(recipient[i]);
          break;
        default:
          break;
      }
    }

    const sendToFewLimit =
      SettingsStore.getSetting('composer', 'send_to_few', 'recipient_limit') || 20;
    const aboveSendToFewLimit = selectionLength >= sendToFewLimit;
    const anotherDistrictIsSelected = districtIsSelected && !recipientIsSelected;

    let disabledCopy;

    if (anotherDistrictIsSelected) {
      disabledCopy = DisabledComposerListItemCopy.DISTRICT_IS_SELECTED;
    } else if (aboveSendToFewLimit && !recipientIsSelected) {
      disabledCopy = DisabledComposerListItemCopy.SEND_TO_FEW_LIMIT_REACHED(sendToFewLimit);
    }

    if (isEmpty(organizationRecipients) === false) {
      return (
        <ComposerContext.Consumer>
          {composerContext => {
            return (
              <OrganizationRecipientItem
                recipient={organizationRecipients[0]}
                recipientIsSelected={recipientIsSelected}
                disabledCopy={
                  nonDistrictRecipientIsSelected
                    ? DisabledComposerListItemCopy.NONDISTRICT_RECIPIENT_IS_SELECTED
                    : disabledCopy
                }
                UIEventContext={composerContext.getUIEventContext()}
              />
            );
          }}
        </ComposerContext.Consumer>
      );
    } else if (isEmpty(groupRecipients) === false) {
      const groupRecipient = groupRecipients[0];
      const currentUser = CurrentUser.getUser();
      const hasFreeProductLimitFeature =
        currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');
      const shouldShowLimitedSendFunctionality =
        hasFreeProductLimitFeature && groupRecipient.group.hasLimitedSendFunctionality;

      if (shouldShowLimitedSendFunctionality) {
        return <LimitedGroupRecipientItem recipient={groupRecipient} />;
      }

      return (
        <GroupRecipientItem
          recipient={groupRecipient}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={disabledCopy}
        />
      );
    } else if (isEmpty(groupChatRecipients) === false) {
      return (
        <GroupChatRecipientItem
          recipient={groupChatRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={disabledCopy}
        />
      );
    } else if (isEmpty(individualRecipients) === false) {
      const { member } = individualRecipients[0];
      const memberName = member.getName();
      const chatIneligibleText = (state: string) => {
        switch (state) {
          case 'under_13':
            return ChatIneligibleCopy.UNDER_13(memberName);
          case 'pending':
            return ChatIneligibleCopy.PENDING(memberName);
          case 'unknown':
            return ChatIneligibleCopy.UNKNOWN(memberName);
          default:
            return ChatIneligibleCopy.UNAVAILABLE(memberName);
        }
      };
      return (
        <IndividualRecipientItem
          recipient={individualRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={
            member.isEligible() ? (
              disabledCopy
            ) : (
              <React.Fragment>
                {chatIneligibleText(member.getState()).h1}
                <Text size="xxxsmall" color="wolf" element="div">
                  {chatIneligibleText(member.getState()).h2}
                </Text>
              </React.Fragment>
            )
          }
        />
      );
    }
    return <ErrorReporter error="empty content in RecipientItem" />;
  }
}
